import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  GraduationCap,
  FileText,
  Calendar,
  Users,
  Clock,
  TrendingUp,
  Globe,
  ArrowRight,
  CheckCircle,
  XCircle,
  Edit,
  Plus,
  Activity
} from 'lucide-react';

const API_URL = window.location.origin;

export const AdminDashboard = () => {
  const { token, user, hasPermission, canApprove, isAdmin, logout } = useCMSAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cms/dashboard/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.status === 401) {
          logout();
          return;
        }
        if (res.ok) {
          const data = await res.json();
          setStats(data);
        }
      } catch (e) {
        console.error('Failed to fetch stats');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token, logout]);

  const actionIcons = {
    login: <Activity className="w-4 h-4 text-ux-cyan" />,
    create: <Plus className="w-4 h-4 text-green-500" />,
    update: <Edit className="w-4 h-4 text-amber-500" />,
    delete: <XCircle className="w-4 h-4 text-red-500" />,
    approve: <CheckCircle className="w-4 h-4 text-green-500" />,
    reject: <XCircle className="w-4 h-4 text-red-500" />
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-ux-cyan border-t-transparent rounded-full" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Welcome Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
              Welcome back, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-slate-600 dark:text-slate-300">
              Here's what's happening with your content today.
            </p>
          </div>
          
          {/* Quick Actions */}
          <div className="flex gap-2">
            {hasPermission('universities', 'write') && (
              <Button asChild size="sm" className="bg-ux-cyan hover:bg-ux-cyan-dark">
                <Link to="/admin/universities/new">
                  <Plus className="w-4 h-4 mr-1" /> Add University
                </Link>
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Universities */}
          <Link to="/admin/universities" className="block">
            <Card className="dark:bg-slate-800 dark:border-slate-700 hover:shadow-lg hover:border-ux-cyan/50 transition-all cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Universities</p>
                    <p className="text-2xl font-bold text-slate-800 dark:text-white">{stats?.universities?.total || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-ux-cyan/10 dark:bg-ux-cyan/20 flex items-center justify-center">
                    <GraduationCap className="w-6 h-6 text-ux-cyan" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Blog Posts */}
          <Link to="/admin/blog" className="block">
            <Card className="dark:bg-slate-800 dark:border-slate-700 hover:shadow-lg hover:border-ux-violet/50 transition-all cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Blog Posts</p>
                    <p className="text-2xl font-bold text-slate-800 dark:text-white">{stats?.blog?.total || 0}</p>
                    <p className="text-xs text-green-500">{stats?.blog?.published || 0} published</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-ux-violet/10 dark:bg-ux-violet/20 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-ux-violet" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Events */}
          <Link to="/admin/events" className="block">
            <Card className="dark:bg-slate-800 dark:border-slate-700 hover:shadow-lg hover:border-ux-coral/50 transition-all cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Events</p>
                    <p className="text-2xl font-bold text-slate-800 dark:text-white">{stats?.events?.total || 0}</p>
                    <p className="text-xs text-ux-coral">{stats?.events?.upcoming || 0} upcoming</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-ux-coral/10 dark:bg-ux-coral/20 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-ux-coral" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Pending Approvals */}
          {canApprove() && (
            <Link to="/admin/pending" className="block">
              <Card className={`dark:bg-slate-800 dark:border-slate-700 hover:shadow-lg transition-all cursor-pointer ${stats?.pending_drafts > 0 ? 'border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 hover:border-amber-400' : 'hover:border-amber-500/50'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Pending Approvals</p>
                      <p className="text-2xl font-bold text-slate-800 dark:text-white">{stats?.pending_drafts || 0}</p>
                      {stats?.pending_drafts > 0 && (
                        <span className="text-xs text-amber-600 dark:text-amber-400">
                          Review now
                        </span>
                      )}
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-amber-500/10 dark:bg-amber-500/20 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-amber-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          )}

          {/* CMS Users (Admin Only) */}
          {isAdmin() && (
            <Link to="/admin/users" className="block">
              <Card className="dark:bg-slate-800 dark:border-slate-700 hover:shadow-lg hover:border-ux-blue/50 transition-all cursor-pointer">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">CMS Users</p>
                      <p className="text-2xl font-bold text-slate-800 dark:text-white">{stats?.cms_users || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-ux-blue/10 dark:bg-ux-blue/20 flex items-center justify-center">
                      <Users className="w-6 h-6 text-ux-blue" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          )}
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Universities by Country */}
          <Card className="lg:col-span-2 dark:bg-slate-800 dark:border-slate-700">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2 text-slate-800 dark:text-white">
                  <Globe className="w-5 h-5 text-ux-cyan" />
                  Universities by Country
                </CardTitle>
                <Button variant="ghost" size="sm" asChild className="dark:text-slate-300 dark:hover:text-white">
                  <Link to="/admin/universities">
                    View all <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats?.universities?.by_country?.map((item) => (
                  <div key={item.country} className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{item.country}</span>
                        <span className="text-sm text-slate-500 dark:text-slate-400">{item.count}</span>
                      </div>
                      <div className="h-2 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-ux-cyan to-ux-violet rounded-full"
                          style={{ width: `${(item.count / stats.universities.total) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-slate-800 dark:text-white">
                <Activity className="w-5 h-5 text-ux-violet" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.recent_activity?.length > 0 ? (
                <div className="space-y-3">
                  {stats.recent_activity.slice(0, 6).map((log) => (
                    <div key={log.id} className="flex items-start gap-3 text-sm">
                      <div className="mt-0.5">
                        {actionIcons[log.action] || <Activity className="w-4 h-4 text-gray-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-700 dark:text-slate-200 truncate">
                          <span className="font-medium">{log.performer_name}</span>
                          <span className="text-slate-500 dark:text-slate-400"> {log.action} </span>
                          <span className="text-slate-600 dark:text-slate-300">{log.entity_name}</span>
                        </p>
                        <p className="text-xs text-slate-400 dark:text-slate-500">
                          {new Date(log.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">
                  No recent activity
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Links */}
        <Card className="dark:bg-slate-800 dark:border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-slate-800 dark:text-white">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {hasPermission('universities', 'read') && (
                <Link 
                  to="/admin/universities"
                  className="p-4 rounded-xl bg-ux-cyan/5 dark:bg-ux-cyan/10 hover:bg-ux-cyan/10 dark:hover:bg-ux-cyan/20 transition-colors text-center group"
                >
                  <GraduationCap className="w-8 h-8 mx-auto mb-2 text-ux-cyan group-hover:scale-110 transition-transform" />
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Manage Universities</p>
                </Link>
              )}
              {hasPermission('blog', 'read') && (
                <Link 
                  to="/admin/blog"
                  className="p-4 rounded-xl bg-ux-violet/5 dark:bg-ux-violet/10 hover:bg-ux-violet/10 dark:hover:bg-ux-violet/20 transition-colors text-center group"
                >
                  <FileText className="w-8 h-8 mx-auto mb-2 text-ux-violet group-hover:scale-110 transition-transform" />
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Manage Blog</p>
                </Link>
              )}
              {hasPermission('events', 'read') && (
                <Link 
                  to="/admin/events"
                  className="p-4 rounded-xl bg-ux-coral/5 dark:bg-ux-coral/10 hover:bg-ux-coral/10 dark:hover:bg-ux-coral/20 transition-colors text-center group"
                >
                  <Calendar className="w-8 h-8 mx-auto mb-2 text-ux-coral group-hover:scale-110 transition-transform" />
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Manage Events</p>
                </Link>
              )}
              {canApprove() && (
                <Link 
                  to="/admin/pending"
                  className="p-4 rounded-xl bg-amber-500/5 dark:bg-amber-500/10 hover:bg-amber-500/10 dark:hover:bg-amber-500/20 transition-colors text-center group"
                >
                  <Clock className="w-8 h-8 mx-auto mb-2 text-amber-500 group-hover:scale-110 transition-transform" />
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Review Pending</p>
                  {stats?.pending_drafts > 0 && (
                    <Badge className="mt-1 bg-amber-500 text-white">{stats.pending_drafts}</Badge>
                  )}
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
